import { useState, useEffect, useRef } from "react";
import "./ResponseContentContact.css";
import { Question } from "effno-core";
import { useSelector } from "react-redux";
import { selectUIStates } from "../../redux/features/appData";
import { ResponseNavigate } from "./ResponseNavigate";
import { ContactResponse, ShortResponse } from "../config/responseSettings";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import config from "environment";
import { ResponseErrors } from "./responseErrors";
import { NEXT_BUTTON } from "./ResponseContent";
import { getCountries, getCountryCallingCode } from 'react-phone-number-input'
import { TriangleAlert } from "lucide-react";
import { validateEmail } from "../shared/responseValidators";

const debugMode = config.debugMode;

type ResponseContentContactProps = {
    question: Question;
    responseValues: string;
    isPreview: boolean;
    isMobileLayout: boolean;
    isLastQuestion: boolean;
    commentMode: boolean;
    onNext?: () => void;
    onPrev?: () => void;
    onSubmit: (responseValues: string) => void;
}

type ContactInfo = {
    firstName: string,
    lastName: string,
    phone: string,
    email: string,
    company: string
}

const CONTACT_DEFAULT : ContactInfo = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    company: ""
}

const enum AutoFocus {
    firstName = "firstName",
    lastName = "lastName",
    phone = "phone",
    email = "email",
    company = "company"
}

export const ResponseContentContact = ({
    question,
    responseValues,
    isPreview,
    isMobileLayout,
    isLastQuestion,
    commentMode,
    onNext,
    onPrev,
    onSubmit
} : ResponseContentContactProps) => {

    // const uiStates = useSelector(selectUIStates);
    const errorRef = useRef(null)

    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [defaultCountryCode, setDefaultCountryCode] = useState<string>("us");
    const [defaultPhoneAsCode, setDefaultPhoneAsCode] = useState<string>("1");

    const [contact, setContact] = useState<ContactInfo>(CONTACT_DEFAULT);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        setTitle(question.title);
        setDescription(question.description);
        const values = JSON.parse(responseValues);
        
        let defaultCountryCode = JSON.parse(question.settings).defaultCountryCode || "us";
        let responseCountryCode = JSON.parse(responseValues).countryCode;

        setContact({
            ...CONTACT_DEFAULT, 
            firstName: values?.firstName || "",
            lastName: values?.lastName || "",
            phone: values?.phone || " ",
            email: values?.email || "",
            company: values?.company || ""
            });


        console.log("[ResponseContentPhone] responseValues: ", responseValues);
        console.log("[ResponseContentPhone] defaultCountryCode: ", defaultCountryCode);
        console.log("[ResponseContentPhone] responseCountryCode: ", responseCountryCode);

        const code = responseCountryCode ? responseCountryCode : defaultCountryCode || "us"
        console.log("[ResponseContentPhone] code: ", code);

        setDefaultCountryCode(code.toLowerCase());
        setDefaultPhoneAsCode(getCountryCallingCode((code as string).toUpperCase() as any));
    
        setError(ResponseErrors.none);
    }, [question, responseValues])


    const metRequirements = (theContact: ContactInfo) : 
        "ok" | "requiredAll" | "requiredFirstName" | "requiredLastName" | "requiredPhone" | 
        "requiredEmail" | "validEmail" | "requiredCompany" | "invalid" => {

        if (question.required && theContact.firstName.trim() === "" && 
            theContact.lastName.trim() === "" && theContact.phone.trim() === "" && 
            theContact.email.trim() === "" && theContact.company.trim() === "") {
            return "requiredAll";
        }

        if (!question.settings) {
            return "invalid";
        }

        const settings = JSON.parse(question.settings);

        if (!settings) {
            return "invalid";
        }

        if (settings.firstNameRequired && theContact.firstName.trim() === "") {
            return "requiredFirstName";
        }

        if (settings.lastNameRequired && theContact.lastName.trim() === "") {
            return "requiredLastName";
        }

        if (settings.phoneRequired && theContact.phone.trim() === "") {
            return "requiredPhone";
        }

        if (settings.emailRequired && theContact.email.trim() === "") {
            return "requiredEmail";
        }

        if (theContact.email.trim() !== "" && !validateEmail(theContact.email.trim())) {
            return "validEmail";
        }

        if (settings.companyRequired && theContact.company.trim() === "") {
            return "requiredCompany";
        }

        return "ok";
    }

    const handleNext = () => {
        if (isPreview && onNext) {
            onNext();
        } else {
            handleSubmit();
        }
    }

    const handleSubmit = () => {
        const met = metRequirements(contact);
        
        if (met === "requiredAll") {
            setAndShowError(ResponseErrors.requiredAll);
            return;
        }

        if (met === "requiredFirstName") {
            setAndShowError(ResponseErrors.firstNameRequired);
            return;
        }

        if (met === "requiredLastName") {
            setAndShowError(ResponseErrors.lastNameRequired);
            return;
        }

        if (met === "requiredPhone") {
            setAndShowError(ResponseErrors.phoneRequired);
            return;
        }

        if (met === "requiredEmail") {
            setAndShowError(ResponseErrors.emailRequired);
            return;
        }

        if (met === "validEmail") {
            setAndShowError(ResponseErrors.validEmail);
            return;
        }


        if (met === "requiredCompany") {
            setAndShowError(ResponseErrors.companyRequired);
            return;
        }

        console.log("[ResponseContentContact] submitting contact: ", contact);

        const newResponseValues: string = JSON.stringify({
            ...contact
        } as ContactResponse);

        onSubmit(newResponseValues);
    }

    const handleKeyDown = (e: any) => {
        
        // if (e.key === "Enter") {
        //     handleSubmit();
        //     return;
        // }

        setError(ResponseErrors.none);
    
    }

    const handleChange = (e: any) => {
        setContact({
            ...contact,
            [e.target.name]: e.target.value
        });
    }

    const handleChangePhone = (newPhone: string) => {
        setContact({
            ...contact,
            phone: newPhone
        });
    }

    const setAndShowError = (error: ResponseErrors) => {
        setError(error);
        scrollErrorIntoView();
    }

    const scrollErrorIntoView = () => {
        if (errorRef.current) {
            const current : any = errorRef.current;
            current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }

    const requirementsMet = metRequirements(contact) === "ok";

    const { 
        firstNameRequired,
        firstNameVisible, 
        lastNameRequired,
        lastNameVisible,
        phoneRequired,
        phoneVisible,
        emailRequired,
        emailVisible,
        companyRequired,
        companyVisible 
    } =  JSON.parse(question.settings || "{}");

    let whichAutoFocus = AutoFocus.firstName;
    
    if (firstNameVisible) {
        whichAutoFocus = AutoFocus.firstName;
    } else if (lastNameVisible) {
        whichAutoFocus = AutoFocus.lastName;
    } else if (phoneVisible) {
        whichAutoFocus = AutoFocus.phone;
    } else if (emailVisible) {
        whichAutoFocus = AutoFocus.email;
    } else if (companyVisible) {
        whichAutoFocus = AutoFocus.company;
    }

    // console.log("[ResponseContentContact] question: ", question);
    console.log("[ResponseContentContact] values: ", responseValues);
    console.log("[ResponseContentContact] contact: ", contact);
    // console.log("[ResponseContentContact] text: ", text);

    const phone = contact.phone;
    console.log("[ResponseContentContact] phone: ", phone);

    return (
        <div className="form-content-body response-content-contact">    

            {/* {debugMode && question &&
                <div className="response-content-question-id">
                    {question.id}
                </div>
            } */}
                        
            <div 
                className="response-content-title"
                data-testid={"response-content-title:" + title}
            >
                {title}
            </div>
            
            <div className="response-content-description">
               {description}
            </div>   

            <div className="response-content-contact">

                {firstNameVisible &&
                    <>
                        <div className="form-content-label">
                            First Name
                            {firstNameRequired && <span>&nbsp;*</span>}

                        </div>
                        <div className="response-content-text">
                            <input 
                                type="text"
                                name="firstName"
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                value={contact.firstName}
                                placeholder="First Name"
                                data-testid="response-content-first"
                                autoFocus={whichAutoFocus === AutoFocus.firstName}
                            />
                        </div>
                    </>
                }

                {lastNameVisible &&
                    <>
                        <div className="response-content-label">
                            Last Name
                            {lastNameRequired && <span>&nbsp;*</span>}
                        </div>

                        <div className="response-content-text">
                            <input 
                                type="text"
                                name="lastName"
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                value={contact.lastName}
                                placeholder="Last Name"
                                data-testid="response-content-last"
                                autoFocus={whichAutoFocus === AutoFocus.lastName}
                            />
                        </div>
                    </>
                }
                 
                {phoneVisible &&
                    <>
                        <div className="response-content-label">
                            Phone
                            {phoneRequired && <span>&nbsp;*</span>}
                        </div>

                        <div 
                            className="response-content-phone-enter"
                            data-testid="response-content-phone"
                        >
                            {phone && phone.length > 0 &&
                                <PhoneInput
                                    country={defaultCountryCode}
                                    disableCountryGuess={false}
                                    value={phone + defaultPhoneAsCode}
                                    enableSearch={true}
                                    onChange={handleChangePhone}
                                    inputStyle={{
                                        backgroundColor: "transparent",
                                        width: "250px",
                                        color: "var(--ftAnswerColor)",
                                        border: "1px solid var(--ftAnswerColor)",
                                        fontSize: "1.25rem"
                                    }}
                                    dropdownStyle={{
                                        backgroundColor: "white",
                                        border: "1px solid var(--ftAnswerColor)",
                                    }}
                                    buttonStyle={{
                                        backgroundColor: "transparent",
                                        border: "1px solid var(--ftAnswerColor)"
                                    }}
                                    searchStyle={{
                                        backgroundColor: "white",
                                        border: "1px solid var(--ftAnswerColor)",
                                        paddingInline: "0px",
                                    }}
                                    inputClass="response-content-phone-input"
                                />  
                            }
                        </div>
                    </>
                }
               
                {emailVisible &&
                    <>
                        <div className="response-content-label">
                            Email
                            {emailRequired && <span>&nbsp;*</span>}
                        </div>

                        <div className="response-content-text">
                            <input 
                                type="text"
                                name="email"
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                value={contact.email}
                                placeholder="Email"
                                data-testid="response-content-email"
                                autoFocus={whichAutoFocus === AutoFocus.email}
                            />
                        </div>
                    </>
                }

                {companyVisible &&
                    <>
                        <div className="response-content-label">
                            Company
                            {companyRequired && <span>&nbsp;*</span>}
                        </div>

                        <div className="response-content-text">
                            <input 
                                type="text"
                                name="company"
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                value={contact.company}
                                placeholder="Company"
                                data-testid="response-content-company"
                                autoFocus={whichAutoFocus === AutoFocus.company}
                            />
                        </div>
                    </>
                }

                <div 
                    className="response-content-error"
                    ref={errorRef}
                    style={!error ? {backgroundColor: "transparent"} : {}}
                    data-testid="response-content-error"
                >
                    {error && <TriangleAlert size={16}/>}{error}
                </div>

                {/* <div 
                    className="response-content-submit"
                >
                    <button
                        className="response-content-button-submit"
                        onClick={handleSubmit}
                        data-testid="response-content-submit"
                    >
                        {onNext ? NEXT_BUTTON : "Submit"}
                    </button>
                </div> */}

                {/* <div 
                    className="response-content-spacer"
                    style={{height: "40px"}}
                >
                    &nbsp;
                </div> */}

                <ResponseNavigate 
                    isMobileLayout={isMobileLayout}
                    isLastQuestion={isLastQuestion}
                    commentMode={commentMode}
                    onSubmit={handleSubmit}
                    onNext={handleNext}
                    onPrev={onPrev}
                />

                 <div 
                    className="response-content-spacer"
                    style={{height: "8px"}}
                >
                    &nbsp;
                </div>
            </div>

            

        </div>
    )
}